// Classic's division switcher (#583, UX §5.2, mockup c): a pill row on the
// page, never in the nav. Styled after SportSwitcher's pill group (rounded-full
// container, the active pill bg-primary). Core (useDivision) decides the
// options and the pick; this only presents them.
//
// The Classic invariant: when the switcher isn't visible (fewer than 2 live
// divisions, or a detail route) this renders null with no wrapper, so the page
// tree is exactly today's.

import { useEffect, useId, useMemo, useRef, type KeyboardEvent } from 'react'
import { useLocation } from 'react-router-dom'
import { useLeague } from '@/contexts/LeagueContext'
import { useSportContext } from '@/contexts/SportContext'
import type { DivisionRef } from '@/services/api/types'
import {
  ALL_DIVISIONS,
  divisionOptions,
  divisionRouteAllowsAll,
  divisionsEnabled,
  isDivisionListRoute,
  liveDivisions,
  type DivisionValue,
  type SiteDivisionSwitcher,
} from '../../../core/division'
import { useLabels } from '../../../core/labels'
import { useT } from '@/i18n'
import { siteClassicMessages } from '@/i18n/catalogs/site-classic'

/**
 * A row's division, in the competition-type tag style (Schedule's badges).
 * Shown only under "All divisions".
 */
export function DivisionTag({ division }: { division: DivisionRef }) {
  return (
    <span className="px-2 py-0.5 rounded text-[11px] font-bold uppercase tracking-wider bg-secondary/50 text-primary border border-secondary whitespace-nowrap">
      {division.name}
    </span>
  )
}

/**
 * A season label without a leading division name, since the division is
 * already shown beside it ("Men's Fall 2026" → "Fall 2026").
 */
export function seasonWithoutDivision(season: string, division: string): string {
  const s = season.trim()
  const d = division.trim().toLowerCase()
  if (!d || !s.toLowerCase().startsWith(`${d} `)) return s
  const rest = s.slice(d.length).trim()
  return rest || s
}

/** Up to this many divisions show as pills; more fall back to a select (UX §5.1). */
export const DIVISION_PILLS_MAX = 5

interface Props {
  division: SiteDivisionSwitcher
  /** Extra classes for the outer wrapper (spacing, alignment). */
  className?: string
  /** Centre the row (the centred heroes on Teams and Players). */
  center?: boolean
  /** Appended to the polite status line, e.g. "12 games". */
  count?: string
  /**
   * Show the status line (Schedule); elsewhere it is for screen readers only.
   * With one division picked it also names the division's public season and
   * offers "See all divisions".
   */
  showStatus?: boolean
}

/**
 * #741: on a cold first visit the tenant arrives a commit before its sport is
 * adopted (SportContext), so the switcher turns visible one frame late and
 * pushed the page down. While that sport is still unresolved, and the
 * tenant's default sport has 2+ live divisions on this list route, reserve
 * the row: the same markup, invisible. Presentation only (no request
 * changes); leagues without divisions still render nothing.
 */
function usePendingRow(division: SiteDivisionSwitcher): SiteDivisionSwitcher | null {
  const { league } = useLeague()
  const { activeSportId } = useSportContext()
  const { pathname } = useLocation()
  return useMemo(() => {
    if (division.visible || activeSportId || !isDivisionListRoute(pathname)) return null
    const live = liveDivisions(league, league?.default_sport_id)
    if (!divisionsEnabled(live)) return null
    return { visible: true, enabled: true, options: divisionOptions(live, divisionRouteAllowsAll(pathname)), value: ALL_DIVISIONS, active: null, live, set: () => {} }
  }, [division.visible, activeSportId, pathname, league])
}

export default function DivisionPills({ division, className = '', center = false, count, showStatus = false }: Props) {
  const pending = usePendingRow(division)
  if (!division.visible || division.options.length < 2) {
    if (!pending) return null
    return <DivisionPillsInner division={pending} className={`${className} invisible`} center={center} count={count} showStatus={showStatus} />
  }
  return <DivisionPillsInner division={division} className={className} center={center} count={count} showStatus={showStatus} />
}

function DivisionPillsInner({ division, className, center, count, showStatus }: Required<Omit<Props, 'count'>> & { count?: string }) {
  const t = useLabels()
  const m = useT(siteClassicMessages).schedule
  const { league } = useLeague()
  const id = useId()
  const labelId = `${id}-label`
  const rowRef = useRef<HTMLDivElement>(null)
  const pillRefs = useRef<Array<HTMLButtonElement | null>>([])
  const { options, value } = division
  const selected = Math.max(0, options.findIndex((o) => o.value === value))
  const label = t.term('division')
  const active = division.active
  const publicSeason = showStatus && active ? (league?.seasons ?? []).find((s) => s.id === active.public_season_id) : undefined
  const parts = [
    value === 'all' ? m.showingAll : m.showing(options[selected]?.label ?? ''),
    publicSeason && active ? seasonWithoutDivision(publicSeason.name ?? String(publicSeason.year), active.name) : null,
    count ?? null,
  ].filter(Boolean)
  const status = parts.join(' · ')

  // Keep the picked pill in view on a scrolling (phone) row, without moving
  // the page: on mount, on a pick, and whenever the row or the pill resizes
  // (#741 review: the row often first overflows only once the web font lands,
  // after the mount effect had already found nothing to scroll).
  useEffect(() => {
    const row = rowRef.current
    const pill = pillRefs.current[selected]
    if (!row || !pill) return
    const reveal = () => {
      if (row.scrollWidth <= row.clientWidth) return
      const r = row.getBoundingClientRect()
      const p = pill.getBoundingClientRect()
      // The row's right edge fades out over 2rem (the mask), so that strip doesn't count as in view.
      if (p.left < r.left || p.right > r.right - 32) row.scrollLeft = Math.max(0, row.scrollLeft + p.left - r.left - 16)
    }
    reveal()
    if (typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(reveal)
    ro.observe(row)
    ro.observe(pill)
    return () => ro.disconnect()
  }, [selected])

  const pick = (v: DivisionValue) => { if (v !== value) division.set(v) }

  // Radio group keys: arrows move and select (wrapping), Home and End jump.
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const n = options.length
    const next = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? (selected + 1) % n
      : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? (selected - 1 + n) % n
      : e.key === 'Home' ? 0
      : e.key === 'End' ? n - 1
      : null
    if (next == null) return
    e.preventDefault()
    pick(options[next].value)
    pillRefs.current[next]?.focus()
  }

  const statusLine = showStatus ? (
    <p className="mt-3 text-sm text-white/60">
      <span aria-live="polite">{status}</span>
      {active && (
        <>
          {' · '}
          <button
            type="button"
            onClick={() => division.set('all')}
            className="font-bold text-white/70 hover:text-white underline underline-offset-4 py-3 -my-3"
          >
            {m.seeAllDivisions}
          </button>
        </>
      )}
    </p>
  ) : (
    <p aria-live="polite" className="sr-only">{status}</p>
  )

  if (division.live.length > DIVISION_PILLS_MAX) {
    const selectId = `${id}-select`
    return (
      <div className={className}>
        {/* A long division name never sets the width (#740): the select shrinks to the row and ellipsises. */}
        <div className={`flex min-w-0 items-center gap-3 ${center ? 'justify-center' : ''}`}>
          <label htmlFor={selectId} className="flex-none text-sm font-bold text-white/60">{label}</label>
          <select
            id={selectId}
            value={String(value)}
            title={options[selected]?.label}
            onChange={(e) => pick(e.target.value === 'all' ? 'all' : Number(e.target.value))}
            className="min-h-[44px] min-w-0 max-w-full sm:max-w-[20rem] text-ellipsis bg-bg-secondary/60 border border-secondary/30 text-white text-base leading-5 sm:text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-primary/50"
          >
            {options.map((o) => <option key={String(o.value)} value={String(o.value)}>{o.label}</option>)}
          </select>
        </div>
        {statusLine}
      </div>
    )
  }

  return (
    <div className={className}>
      <div className={`flex items-center gap-3 ${center ? 'sm:justify-center' : ''}`}>
        <span id={labelId} className="hidden sm:block flex-none text-xs font-bold uppercase tracking-[0.1em] text-white/45">{label}</span>
        <div className="min-w-0 -mx-4 sm:mx-0 sm:max-w-full flex-1 sm:flex-initial">
          <div
            ref={rowRef}
            role="radiogroup"
            aria-labelledby={labelId}
            onKeyDown={onKeyDown}
            className="flex sm:inline-flex items-center gap-1.5 sm:gap-1 max-w-full overflow-x-auto scrollbar-hide px-4 py-0.5 sm:p-1 sm:rounded-full sm:border sm:border-secondary/40 sm:bg-bg-secondary/40 sm:backdrop-blur-md text-xs [mask-image:linear-gradient(to_right,#000_calc(100%-2rem),transparent)] sm:[mask-image:none]"
          >
            {options.map((o, i) => {
              const on = i === selected
              return (
                <button
                  key={String(o.value)}
                  ref={(el) => { pillRefs.current[i] = el }}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  tabIndex={on ? 0 : -1}
                  onClick={() => pick(o.value)}
                  title={o.label}
                  className={`relative flex-none max-w-[16rem] overflow-hidden text-ellipsis whitespace-nowrap rounded-full px-4 min-h-[44px] sm:min-h-[36px] font-bold uppercase tracking-wider transition-colors border sm:border-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white sm:before:absolute sm:before:inset-x-0 sm:before:-inset-y-1 sm:before:content-[''] ${
                    on
                      ? 'bg-primary text-bg-primary border-transparent'
                      : 'bg-bg-secondary/45 sm:bg-transparent border-secondary/50 text-white/70 hover:text-white'
                  }`}
                >
                  {o.label}
                </button>
              )
            })}
            <span aria-hidden="true" className="w-2 flex-none sm:hidden" />
          </div>
        </div>
      </div>
      {statusLine}
    </div>
  )
}
